/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { envUtil } from "dc-core";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";
import { getTransformMatrixFromPageGeometry } from "dc-preview-utils/src/Utils/PageUtils";
import { isSharedFile } from "utils/DocumentUtils";
import { isIE, isEdge } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { getDevicePixelRatio } from "dc-preview-utils/src/Utils/DeviceUtils";
import { isPersonalCommentingEnabled } from "utils/FeatureFlagUtils";
import { getScrollbarSize } from "selectors/appUiStateSelectors";
import { getDCAPIRenditionSize } from "selectors/dcapiSelectors";
import * as constants from "constants/constants";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { isUndefined, isArray } from "utils/lodashUtils";
import { getShouldUseModernViewer } from "selectors/appSelectors";
import { getTopUIControls, getBottomUIControls } from "selectors/skinnyRailSelectors";
import { rhpDropinIds } from "constants/enums";
import * as stores from "stores";
import {
  isSABAvailable,
  addSABOriginTrialMetaTag,
  getAssetUri
} from "../providers/previewProvider/PreviewProviderUtils";
import {
  getMaxZoomFromAllPages,
  getMinZoomFromAllPages,
  calculateMaxZoomForIEEdge,
  calculateZoomLevelAccToCurrPageRasterSize
} from "providers/previewProvider/ZoomUtilsLite";

export const shouldUseLinearization = props => {
  const disableLinearization = getQueryParameterFromUrl(window.location.href, "disableLinearization");
  return props.config.enableLinearization && disableLinearization !== "true";
};

export const getQueryParamValue = key => getQueryParameterFromUrl(window.location.href, key);

export const getAssetType = () => {
  let path = getQueryParameterFromUrl(window.location.href, "path");
  if (!path) {
    if (isSharedFile()) {
      path = "/review";
    }
  } else {
    path = decodeURIComponent(path);
  }
  return path;
};

export const getTitleFromConfigProp = config => {
  const { docTitle } = config;
  return docTitle;
};

export const isFillSignEnabled = async (config = {}) => {
  const isModernViewerEnabled = getShouldUseModernViewer(stores);
  return isModernViewerEnabled && config.enableFillSign;
};

export const isPdfRequestSignaturesEnabled = async (config = {}) => {
  const isModernViewerEnabled = getShouldUseModernViewer(stores);
  return isModernViewerEnabled && config.enablePdfRequestSignatures;
};

export const getFixedWidthValues = (stores, config) => {
  let { fixedWidthLeftMargin, fixedWidthRightMargin } = config || {};
  if (isUndefined(fixedWidthLeftMargin) && isUndefined(fixedWidthRightMargin)) {
    // default value
    if (isPersonalCommentingEnabled(stores)) {
      fixedWidthLeftMargin = 0;
      fixedWidthRightMargin = 325;
      if (getScrollbarSize(stores) === 0) {
        fixedWidthRightMargin += 15;
      }
    }
  }
  return { fixedWidthLeftMargin, fixedWidthRightMargin };
};

export const getPageHeightAndWidthFromPageGeometry = (stores, data) => {
  let currPageIndex = getCurrentPageIndex(stores);
  let currPageHeight = constants.DEFAULT_PAGE_HEIGHT,
    currPageWidth = constants.DEFAULT_PAGE_HEIGHT;
  if (data.pages) {
    const geometries = data.pages;
    currPageHeight = parseFloat(geometries[currPageIndex] ? geometries[currPageIndex].height : geometries[0].height);
    currPageWidth = parseFloat(geometries[currPageIndex] ? geometries[currPageIndex].width : geometries[0].width);
  }
  return { currPageHeight, currPageWidth };
};

export const getOriginalPageDimensions = (pageGeometries, numPages, index) => {
  let origHeight, origWidth, minZoom, maxZoom;
  let tMatrix = {};
  let mediaBox = [];
  let cropBox = [];
  if (isArray(pageGeometries)) {
    const pageGeometry = pageGeometries.length < numPages ? pageGeometries[0] : pageGeometries[index];
    const boxes = pageGeometry.boxes;
    origHeight = parseFloat(pageGeometry.height);
    origWidth = parseFloat(pageGeometry.width);
    minZoom = pageGeometry.minZoom ? parseFloat(pageGeometry.minZoom) : constants.DEFAULT_MIN_ZOOM;
    maxZoom = pageGeometry.maxZoom ? parseFloat(pageGeometry.maxZoom) : constants.DEFAULT_MAX_ZOOM;
    tMatrix = getTransformMatrixFromPageGeometry(pageGeometry);
    mediaBox = boxes.mediaBox && boxes.mediaBox.length > 0 ? boxes.mediaBox : [];
    cropBox = boxes.cropBox && boxes.cropBox.length > 0 ? boxes.cropBox : [];
  } else {
    origHeight = pageGeometries.height;
    origWidth = pageGeometries.width;
  }
  return { origHeight, origWidth, tMatrix, minZoom, maxZoom, boxes: { cropBox, mediaBox } };
};

const isValidDropinForRHP = dropinId => Object.keys(rhpDropinIds).some(key => rhpDropinIds[key] === dropinId);

const getSelectedDropinFromQueryParam = queryParam => {
  const selectedDropin = getQueryParameterFromUrl(window.location.href, queryParam);
  if (!selectedDropin || typeof selectedDropin !== "string") return "";
  return selectedDropin;
};

export const getSelectedDropinForRHP = selectedDropinFromProp => {
  const selectedDropinFromQueryParam = getSelectedDropinFromQueryParam("defaultRHPFeature");
  const selectedDropinFromAppConfig = isValidDropinForRHP(selectedDropinFromProp) ? selectedDropinFromProp : "";
  return selectedDropinFromQueryParam !== "" ? selectedDropinFromQueryParam : selectedDropinFromAppConfig;
};

export const isConnectorAsset = assetType =>
  assetType === "/onedrive" || assetType === "/sharepoint" || assetType === "/gdrive";

export const getConnectorType = () => {
  const assetType = getAssetType();
  if (isConnectorAsset(assetType)) {
    if (assetType === "/onedrive") {
      return "OneDrive";
    }
    if (assetType === "/sharepoint") {
      return "SharePoint";
    }
    if (assetType === "/gdrive") {
      return "GDrive";
    }
  }
  return undefined;
};

export {
  isSABAvailable,
  addSABOriginTrialMetaTag,
  getAssetUri,
  calculateMaxZoomForIEEdge,
  getMaxZoomFromAllPages,
  getMinZoomFromAllPages,
  calculateZoomLevelAccToCurrPageRasterSize
};
